'use client';

import { c as _c } from "react/compiler-runtime";
import type { QueryClient, QueryKey, UseMutationOptions, UseQueryResult } from '@tanstack/react-query';
import { skipToken, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { MOCK_SUPPORTER, QUERY_KEYS } from '../constants.ts';
export const useReadyForQueries = () => {
  const $ = _c(4);
  const {
    authUser
  } = useAuth("authUser");
  const t0 = !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId;
  const t1 = authUser?.authUserGlobalId ?? MOCK_SUPPORTER?.globalId;
  const t2 = authUser?.authUserPrimaryContactId;
  let t3;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2) {
    t3 = {
      isReady: t0,
      globalId: t1,
      contactId: t2
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const useDataPrimer = globalId => {
  const $ = _c(6);
  let t0;
  if ($[0] !== globalId) {
    t0 = [QUERY_KEYS.dataPrimerReadOnly, globalId];
    $[0] = globalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== queryKey) {
    t1 = {
      queryKey,
      queryFn: skipToken,
      enabled: false
    };
    $[2] = queryKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const primerQuery = useQuery(t1);
  const t2 = !!MOCK_SUPPORTER || primerQuery.status !== "pending" && primerQuery.fetchStatus !== "fetching";
  let t3;
  if ($[4] !== t2) {
    t3 = {
      isPrimerReady: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
type AnySupporterQuery<TData = unknown> = {
  queryKey?: QueryKey;
  queryFn: (globalId: string, queryContext?: {
    queryClient: QueryClient;
  }) => Promise<TData>;
  waitForPrimer?: boolean;
};
type SupporterMutationOptions<TData, TError, TVariables, TContext> = UseMutationOptions<TData, TError, TVariables, TContext> & {
  mockMutationFn?: NonNullable<UseMutationOptions<TData, TError, TVariables, TContext>['mutationFn']>;
  mockOnSuccess?: NonNullable<UseMutationOptions<TData, TError, TVariables, TContext>['onSuccess']>;
};
type InferSupporterQueryData<TQuery extends AnySupporterQuery> = Awaited<ReturnType<TQuery['queryFn']>>;
type SupporterQueryTupleData<TQueries extends readonly AnySupporterQuery[]> = { [K in keyof TQueries]: TQueries[K] extends AnySupporterQuery ? InferSupporterQueryData<TQueries[K]> | undefined : never };
type SupporterQueryTupleResult<TQueries extends readonly AnySupporterQuery[]> = {
  data: SupporterQueryTupleData<TQueries>;
  isFetched: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isPending: boolean;
  isPaused: boolean;
  isError: boolean;
  isSuccess: boolean;
  queries: ReturnType<typeof useQueries>;
};
export function useSupporterQuery<TQuery extends AnySupporterQuery>(supporterQueries: TQuery): UseQueryResult<InferSupporterQueryData<TQuery>>;
export function useSupporterQuery<TQueries extends readonly AnySupporterQuery[]>(supporterQueries: TQueries): SupporterQueryTupleResult<TQueries>;
export function useSupporterQuery(supporterQueries) {
  const $ = _c(41);
  const {
    isReady,
    globalId
  } = useReadyForQueries();
  const {
    isPrimerReady
  } = useDataPrimer(globalId);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== supporterQueries) {
    t0 = Array.isArray(supporterQueries) ? supporterQueries : [supporterQueries];
    $[0] = supporterQueries;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queries = t0;
  let t1;
  if ($[2] !== globalId || $[3] !== isPrimerReady || $[4] !== isReady || $[5] !== queries || $[6] !== queryClient) {
    let t2;
    if ($[8] !== globalId || $[9] !== isPrimerReady || $[10] !== isReady || $[11] !== queryClient) {
      t2 = supporterQuery => ({
        queryKey: [...(supporterQuery.queryKey ?? []), globalId],
        queryFn: async () => {
          if (MOCK_SUPPORTER) {
            await new Promise(_temp);
          }
          return supporterQuery.queryFn(globalId as string, {
            queryClient
          });
        },
        enabled: !!isReady && (!supporterQuery.waitForPrimer || isPrimerReady),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retryOnMount: false
      });
      $[8] = globalId;
      $[9] = isPrimerReady;
      $[10] = isReady;
      $[11] = queryClient;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = queries.map(t2);
    $[2] = globalId;
    $[3] = isPrimerReady;
    $[4] = isReady;
    $[5] = queries;
    $[6] = queryClient;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = {
      queries: t1
    };
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const queryResults = useQueries(t2);
  if (!Array.isArray(supporterQueries)) {
    return queryResults[0] as UseQueryResult<unknown>;
  }
  let t3;
  if ($[15] !== queryResults) {
    t3 = queryResults.map(_temp2);
    $[15] = queryResults;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== queryResults) {
    t4 = queryResults.every(_temp3);
    $[17] = queryResults;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== queryResults) {
    t5 = queryResults.some(_temp4);
    $[19] = queryResults;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== queryResults) {
    t6 = queryResults.some(_temp5);
    $[21] = queryResults;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== queryResults) {
    t7 = queryResults.some(_temp6);
    $[23] = queryResults;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== queryResults) {
    t8 = queryResults.some(_temp7);
    $[25] = queryResults;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== queryResults) {
    t9 = queryResults.some(_temp8);
    $[27] = queryResults;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== queryResults) {
    t10 = queryResults.every(_temp9);
    $[29] = queryResults;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== queryResults || $[32] !== t10 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t11 = {
      data: t3,
      isFetched: t4,
      isFetching: t5,
      isLoading: t6,
      isPending: t7,
      isPaused: t8,
      isError: t9,
      isSuccess: t10,
      queries: queryResults
    };
    $[31] = queryResults;
    $[32] = t10;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11 as SupporterQueryTupleResult<readonly AnySupporterQuery[]>;
}
function _temp9(result_6) {
  return result_6.isSuccess;
}
function _temp8(result_5) {
  return result_5.isError;
}
function _temp7(result_4) {
  return result_4.isPaused;
}
function _temp6(result_3) {
  return result_3.isPending;
}
function _temp5(result_2) {
  return result_2.isLoading;
}
function _temp4(result_1) {
  return result_1.isFetching;
}
function _temp3(result_0) {
  return result_0.isFetched;
}
function _temp2(result) {
  return result.data;
}
function _temp(resolve) {
  return setTimeout(resolve, 1000);
}
export const useSupporterMutation = options => {
  const $ = _c(11);
  let mockMutationFn;
  let mockOnSuccess;
  let mutationOptions;
  if ($[0] !== options) {
    ({
      mockMutationFn,
      mockOnSuccess,
      ...mutationOptions
    } = options);
    $[0] = options;
    $[1] = mockMutationFn;
    $[2] = mockOnSuccess;
    $[3] = mutationOptions;
  } else {
    mockMutationFn = $[1];
    mockOnSuccess = $[2];
    mutationOptions = $[3];
  }
  let t0;
  if ($[4] !== mockMutationFn || $[5] !== mockOnSuccess || $[6] !== mutationOptions) {
    t0 = !!MOCK_SUPPORTER && {
      mutationFn: async (variables, context) => {
        await new Promise(_temp10);
        if (mockMutationFn) {
          return mockMutationFn(variables, context);
        }
        return true as TData;
      },
      onSuccess: mockOnSuccess ?? (mockMutationFn ? mutationOptions.onSuccess : _temp11)
    };
    $[4] = mockMutationFn;
    $[5] = mockOnSuccess;
    $[6] = mutationOptions;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  let t1;
  if ($[8] !== mutationOptions || $[9] !== t0) {
    t1 = {
      ...mutationOptions,
      ...t0
    };
    $[8] = mutationOptions;
    $[9] = t0;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return useMutation(t1);
};
function _temp10(resolve) {
  return setTimeout(resolve, 1000);
}
function _temp11() {
  return alert("Success!");
}